// Multi-territory Upload table column definitions and search fields
// Matches India upload columns exactly: Load ID, Source, Data type, File name, Status, Data count, Uploaded by, Uploaded date
import type {
    MTUploadDataType,
    MTUploadSource,
} from '@/multi-territory/mt-types/mt-upload'
import { buildMTSearchField } from '@/multi-territory/mt-utils/mt-search-fields'
import { Box, Chip, Tooltip } from '@mui/material'
import { formatMTHeaderLabel } from '../mt-utils/mt-search-fields'

const getStatusColor = (status: string) => {
  switch (status?.toUpperCase()) {
    case 'UPLOADED':
    case 'COMPLETE':
    case 'COMPLETED':
      return { backgroundColor: '#F0FDF3', color: '#197C37' }
    case 'FAILED':
    case 'DELETED':
      return { backgroundColor: '#FEF3F2', color: '#E0301E' }
    case 'IN PROGRESS':
    case 'IN_PROGRESS':
    case 'PARTIAL':
    case 'PARTIALLY UPLOADED':
      return { backgroundColor: '#FFDFC4', color: '#D17300' }
    default:
      return { backgroundColor: '#E2E3E5', color: '#495057' }
  }
}

export const getMTUploadColumns = (
  sources: MTUploadSource[] = [],
  dataTypes: MTUploadDataType[] = []
) => [
  {
    id: 'loadId',
    label: 'Load ID',

    align: 'left',
  },
  {
    id: 'transformationType',
    label: 'Source',

    type: 'dropdown',
    align: 'left',
    options: sources.map(src => ({ label: src.value, value: src.value })),
    // When the file name is blank, do not show the Source column value
    render: (value: any, row: any) => (row?.fileName ? value : ''),
  },
  {
    id: 'dataType',
    label: 'Data type',

    type: 'dropdown',
    align: 'left',
    options: dataTypes.map(type => ({ label: type.label, value: type.value })),
  },
  {
    id: 'fileName',
    label: 'File name',

    align: 'left',
    render: (value: any) => {
      if (Array.isArray(value)) {
        if (value.length === 1) {
          return (
            <span
              style={{
                whiteSpace: 'normal',
                wordBreak: 'normal',
                overflowWrap: 'break-word',
                display: 'block',
              }}
            >
              {value[0]}
            </span>
          )
        }
        return (
          <Tooltip
            title={
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {value.map((name: string, idx: number) => (
                  <span key={idx}>{name}</span>
                ))}
              </Box>
            }
            arrow
            placement="top"
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <span
                style={{
                  whiteSpace: 'normal',
                  wordBreak: 'normal',
                  overflowWrap: 'break-word',
                }}
              >
                {value[0]}
              </span>
              <Chip
                label={`+${value.length - 1}`}
                size="small"
                sx={{
                  fontSize: '11px',
                  height: '20px',
                  backgroundColor: '#e0e0e0',
                }}
              />
            </Box>
          </Tooltip>
        )
      }
      return (
        <span
          style={{
            whiteSpace: 'normal',
            wordBreak: 'normal',
            overflowWrap: 'break-word',
            display: 'block',
          }}
        >
          {value || ''}
        </span>
      )
    },
  },
  {
    id: 'status',
    label: 'Status',

    align: 'center',
    render: (value: any, row: any) => {
      const { backgroundColor, color } = getStatusColor(value)
      const badge = (
        <span
          style={{
            backgroundColor,
            color,
            padding: '2px 6px',
            borderRadius: 4,
            display: 'inline-block',
            cursor: 'pointer',
            fontSize: '12px',
          }}
        >
          {value || 'N/A'}
        </span>
      )
      const tooltipText = row?.statusMessage || ''
      if (tooltipText) {
        return (
          <Tooltip title={tooltipText} arrow placement="top">
            <span>{badge}</span>
          </Tooltip>
        )
      }
      return badge
    },
  },
  {
    id: 'dataCount',
    label: 'Data count',

    align: 'center',
  },
  {
    id: 'initiatedBy',
    label: 'Uploaded by',

    align: 'left',
  },
  {
    id: 'startedAt',
    label: 'Uploaded date',

    align: 'left',
  },
]

// Advance-search field labels mirror the Upload table column headers exactly
// (sentence case / "running text", acronyms such as ID kept uppercase) via the
// shared MT header formatter, so the search dropdown and the grid read the same.
export const getMTUploadSearchFields = (
  sources: MTUploadSource[] = [],
  dataTypes: MTUploadDataType[] = []
) => [
  {
    id: 'loadId',
    label: formatMTHeaderLabel('Load ID'),
    type: 'text',
    placeholder: `Enter ${formatMTHeaderLabel('Load ID')}`,
  },
  {
    id: 'transformationType',
    label: formatMTHeaderLabel('Source'),
    type: 'dropdown',
    placeholder: `Select ${formatMTHeaderLabel('Source')}`,
    options: sources.map(src => ({ label: src.value, value: src.value })),
  },
  {
    id: 'dataType',
    label: formatMTHeaderLabel('Data type'),
    type: 'dropdown',
    placeholder: `Select ${formatMTHeaderLabel('Data type')}`,
    options: dataTypes.map(type => ({ label: type.label, value: type.value })),
  },
  {
    id: 'fileName',
    label: formatMTHeaderLabel('File name'),
    type: 'text',
    placeholder: `Enter ${formatMTHeaderLabel('File name')}`,
  },
  {
    id: 'status',
    label: formatMTHeaderLabel('Status'),
    type: 'dropdown',
    placeholder: `Select ${formatMTHeaderLabel('Status')}`,
    // Options mirror the distinct display labels returned by the backend status
    // enum (UPLOADING/UPLOADED → "In progress", FAILED_* → "Failed", etc.) so
    // the filter matches exactly what is shown in the Status column.
    options: [
      { label: 'In progress', value: 'In progress' },
      { label: 'Uploaded', value: 'Uploaded' },
      { label: 'Validating', value: 'Validating' },
      { label: 'Failed', value: 'Failed' },
      { label: 'Partially Uploaded', value: 'Partially Uploaded' },
      { label: 'Deleted', value: 'Deleted' },
    ],
  },
  {
    id: 'dataCount',
    label: formatMTHeaderLabel('Data count'),
    type: 'text',
    placeholder: `Enter ${formatMTHeaderLabel('Data count')}`,
  },
  {
    id: 'initiatedBy',
    label: formatMTHeaderLabel('Uploaded by'),
    type: 'text',
    placeholder: `Enter ${formatMTHeaderLabel('Uploaded by')}`,
  },
  // "Uploaded date" is a date column — render a date picker (not free text) in
  // advance search. buildMTSearchField sets type='date' + a short DD/MM/YYYY
  // placeholder for the native date input handled by components/common/Search.
  buildMTSearchField({
    id: 'startedAt',
    label: formatMTHeaderLabel('Uploaded date'),
  }),
]
